/**
 * EMERGENCY BUG MONITORING DASHBOARD
 * Allows admins to detect, monitor, and fix the critical mobile submission bug
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJson } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { RefreshCw, AlertTriangle, CheckCircle, Bug, Wrench, AlertCircle, ExternalLink, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface InvalidTask {
  taskId: number;
  operatorName: string;
  storeName: string;
  campaignName: string;
  targetQuantity: number;
  status: string;
  updatedAt: string;
  evidenceAssets?: Array<{
    id: number;
    assetType: string;
    secureUrl: string;
    uploadedAt: string;
  }>;
  postingEvents?: Array<{
    id: number;
    mediaFormatId: number;
    postedUnits: number;
    notes?: string;
    createdAt: string;
  }>;
  mediaRequirements?: Array<{
    formatName: string;
    quantity: number;
    type: string;
  }>;
}

interface SystemHealth {
  timestamp: string;
  systemHealthy: boolean;
  criticalBugActive: boolean;
  invalidTaskCount: number;
  preventionActive: boolean;
  detectionActive: boolean;
  correctionAvailable: boolean;
}

interface InvalidTaskResponse {
  invalidTasks: InvalidTask[];
}

export default function EvidenceMonitor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [filters, setFilters] = useState({
    operator: '',
    store: '',
    campaign: ''
  });
  const [sortBy, setSortBy] = useState<'updatedAt' | 'taskId' | 'operator'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Query for system health
  const { data: healthData, isLoading: healthLoading, error: healthError } = useQuery<SystemHealth>({
    queryKey: ['/api/emergency/system-health'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/emergency/system-health');
      const data = await getJson<SystemHealth>('/api/emergency/system-health');
      console.log('[QUERY_DEBUG] /api/emergency/system-health response:', data);
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Query for invalid tasks
  const { data: invalidTasksData, isLoading: tasksLoading, error: tasksError } = useQuery<InvalidTaskResponse>({
    queryKey: ['/api/emergency/detect-invalid-tasks'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/emergency/detect-invalid-tasks');
      const data = await getJson<InvalidTaskResponse>('/api/emergency/detect-invalid-tasks');
      console.log('[QUERY_DEBUG] /api/emergency/detect-invalid-tasks response:', data?.invalidTasks?.length || 0, 'invalid tasks');
      return data;
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Mutation to correct invalid tasks
  const correctTasksMutation = useMutation({
    mutationFn: async (taskIds: number[]) => {
      const response = await fetch('/api/emergency/correct-invalid-tasks', {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ taskIds }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to correct tasks');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Tasks Corrected Successfully',
        description: `${data.correctedTaskCount} tasks have been reset to ready_for_operator status`,
      });
      setSelectedTasks([]);
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/emergency/detect-invalid-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/emergency/system-health'] });
    },
    onError: (error) => {
      toast({
        title: 'Correction Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/emergency/detect-invalid-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['/api/emergency/system-health'] });
    toast({
      title: 'Data Refreshed',
      description: 'System health and task validation data updated',
    });
  };

  const handleTaskSelection = (taskId: number) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (!hasInvalidTasks || !invalidTasks) return;
    
    const allTaskIds = invalidTasks.map((task: InvalidTask) => task.taskId);
    setSelectedTasks(prev => 
      prev.length === allTaskIds.length ? [] : allTaskIds
    );
  };

  const handleCorrectTasks = () => {
    if (selectedTasks.length === 0) {
      toast({
        title: 'No Tasks Selected',
        description: 'Please select tasks to correct',
        variant: 'destructive',
      });
      return;
    }
    
    setShowConfirmModal(true);
  };

  const confirmCorrection = () => {
    setShowConfirmModal(false);
    correctTasksMutation.mutate(selectedTasks);
  };

  const health: SystemHealth | undefined = healthData;
  const allInvalidTasks: InvalidTask[] = invalidTasksData?.invalidTasks || [];
  
  // Apply filters and sorting
  const filteredTasks = allInvalidTasks
    .filter(task => {
      if (filters.operator && !task.operatorName?.toLowerCase().includes(filters.operator.toLowerCase())) return false;
      if (filters.store && !task.storeName?.toLowerCase().includes(filters.store.toLowerCase())) return false;
      if (filters.campaign && !task.campaignName?.toLowerCase().includes(filters.campaign.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';
      
      switch (sortBy) {
        case 'updatedAt':
          aValue = new Date(a.updatedAt).getTime();
          bValue = new Date(b.updatedAt).getTime();
          break;
        case 'taskId':
          aValue = a.taskId;
          bValue = b.taskId;
          break;
        case 'operator':
          aValue = a.operatorName || '';
          bValue = b.operatorName || '';
          break;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  
  const invalidTasks = filteredTasks;

  const hasInvalidTasks = invalidTasks.length > 0;

  if (healthError || tasksError) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Emergency Dashboard</AlertTitle>
          <AlertDescription>
            {healthError?.message || tasksError?.message || 'Failed to load emergency data'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Emergency Bug Monitor</h1>
          <p className="text-muted-foreground">
            Monitor and fix tasks marked operator_complete without posting events
          </p>
        </div>
        <Button 
          onClick={handleRefresh}
          disabled={healthLoading || tasksLoading}
          variant="outline"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${healthLoading || tasksLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* System Health Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {health?.systemHealthy ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            System Health Status
          </CardTitle>
          <CardDescription>
            {health?.timestamp && `Last checked: ${new Date(health.timestamp).toLocaleString()}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Overall Health</div>
              <Badge variant={health?.systemHealthy ? 'default' : 'destructive'}>
                {health?.systemHealthy ? 'Healthy' : 'Critical Bug Active'}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Invalid Tasks</div>
              <Badge variant={health?.invalidTaskCount === 0 ? 'secondary' : 'destructive'}>
                {health?.invalidTaskCount || 0} Found
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Prevention</div>
              <Badge variant={health?.preventionActive ? 'default' : 'secondary'}>
                {health?.preventionActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Detection</div>
              <Badge variant={health?.detectionActive ? 'default' : 'secondary'}>
                {health?.detectionActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Critical Alert */}
      {health?.criticalBugActive && (
        <Alert variant="destructive">
          <Bug className="h-4 w-4" />
          <AlertTitle>🚨 Critical Bug Detected</AlertTitle>
          <AlertDescription>
            {health.invalidTaskCount} tasks are marked operator_complete but have no posting events. 
            These tasks need immediate correction to prevent data integrity issues.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Controls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium">Filter by Operator</label>
              <input
                type="text"
                placeholder="Search operators..."
                value={filters.operator}
                onChange={(e) => setFilters(prev => ({ ...prev, operator: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Filter by Store</label>
              <input
                type="text"
                placeholder="Search stores..."
                value={filters.store}
                onChange={(e) => setFilters(prev => ({ ...prev, store: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Filter by Campaign</label>
              <input
                type="text"
                placeholder="Search campaigns..."
                value={filters.campaign}
                onChange={(e) => setFilters(prev => ({ ...prev, campaign: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Sort By</label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as 'updatedAt' | 'taskId' | 'operator');
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              >
                <option value="updatedAt-desc">Updated Time (Latest)</option>
                <option value="updatedAt-asc">Updated Time (Oldest)</option>
                <option value="taskId-asc">Task ID (Low to High)</option>
                <option value="taskId-desc">Task ID (High to Low)</option>
                <option value="operator-asc">Operator (A-Z)</option>
                <option value="operator-desc">Operator (Z-A)</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ operator: '', store: '', campaign: '' })}
            >
              Clear Filters
            </Button>
            <div className="text-sm text-muted-foreground">
              Showing {invalidTasks.length} of {allInvalidTasks.length} tasks
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invalid Tasks Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Invalid Tasks ({invalidTasks.length})
          </CardTitle>
          <CardDescription>
            Tasks marked operator_complete without posting events - filtered and sorted results
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invalidTasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              No invalid tasks detected. System integrity is intact.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selection Controls */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedTasks.length === invalidTasks.length ? 'Deselect All' : 'Select All'}
                </Button>
                <div className="text-sm text-muted-foreground">
                  {selectedTasks.length} of {invalidTasks.length} tasks selected
                </div>
                {selectedTasks.length > 0 && (
                  <Button
                    onClick={handleCorrectTasks}
                    disabled={correctTasksMutation.isPending}
                    variant="destructive"
                    size="sm"
                  >
                    {correctTasksMutation.isPending ? 'Correcting...' : `Correct ${selectedTasks.length} Tasks`}
                  </Button>
                )}
              </div>

              <Separator />

              {/* Task List */}
              <div className="space-y-2">
                {invalidTasks.map((task) => (
                  <div
                    key={task.taskId}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedTasks.includes(task.taskId)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => handleTaskSelection(task.taskId)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="font-medium">Task #{task.taskId}</div>
                        <div className="text-sm text-muted-foreground grid grid-cols-2 gap-2">
                          <div><strong>Campaign:</strong> {task.campaignName}</div>
                          <div><strong>Store:</strong> {task.storeName}</div>
                          <div><strong>Operator:</strong> {task.operatorName}</div>
                          <div><strong>Target Quantity:</strong> {task.targetQuantity}</div>
                          <div><strong>Posted Units:</strong> {(task as any).postedUnits || 0}</div>
                          <div><strong>Photo Count:</strong> {(task as any).photoCount || 0}</div>
                          <div><strong>Signature:</strong> 
                            <span className={`ml-1 px-1 rounded text-xs ${
                              (task as any).signatureStatus === 'PRESENT' ? 'bg-green-100 text-green-800' :
                              (task as any).signatureStatus === 'MISSING' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {(task as any).signatureStatus || 'UNKNOWN'}
                            </span>
                          </div>
                          <div><strong>Failure Reason:</strong> 
                            <span className="ml-1 px-1 bg-red-100 text-red-800 rounded text-xs">
                              {(task as any).validationFailureReason || 'NO_POSTING_EVENT'}
                            </span>
                          </div>
                          <div className="col-span-2"><strong>Updated:</strong> {new Date(task.updatedAt).toLocaleString()}</div>
                        </div>
                        
                        {/* Evidence Display */}
                        {(task as any).evidence && ((task as any).evidence.evidenceCount > 0) && (
                          <div className="bg-green-50 border border-green-200 rounded p-3 mt-2">
                            <h4 className="font-medium text-green-800 mb-2 text-sm">Evidence Found ({(task as any).evidence.evidenceCount}):</h4>
                            
                            {(task as any).evidence.photos.length > 0 && (
                              <div className="mb-3">
                                <h5 className="font-medium text-xs text-green-700 mb-2">
                                  Photos ({(task as any).evidence.photos.length}):
                                </h5>
                                <div className="grid grid-cols-3 gap-2">
                                  {(task as any).evidence.photos.slice(0, 3).map((photo: any) => (
                                    <div key={photo.id} className="bg-white rounded border">
                                      <img 
                                        src={photo.secureUrl}  
                                        alt={photo.fileName}
                                        className="w-full h-16 object-cover rounded"
                                        onError={(e) => {
                                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNmM2Y0ZjYiLz48dGV4dCB4PSIzMiIgeT0iMzIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM2NzcwOGYiIGZvbnQtc2l6ZT0iMTAiPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                                {(task as any).evidence.photos.length > 3 && (
                                  <p className="text-xs text-green-600 mt-1">+{(task as any).evidence.photos.length - 3} more photos</p>
                                )}
                              </div>
                            )}

                            {/* Store Manager Signature - from store_visits (per store-cycle) */}
                            {(task as any).storeSignature?.url && (
                              <div>
                                <h5 className="font-medium text-xs text-green-700 mb-2">
                                  Store Signature:
                                </h5>
                                <div className="bg-white rounded border p-1">
                                  <img 
                                    src={(task as any).storeSignature.url} 
                                    alt="Store Manager Signature"
                                    className="w-full h-12 object-contain rounded"
                                    style={{ backgroundColor: 'white' }}
                                    onError={(e) => {
                                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA2NCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNDgiIGZpbGw9IiNmM2Y0ZjYiLz48dGV4dCB4PSIzMiIgeT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM2NzcwOGYiIGZvbnQtc2l6ZT0iOCI+U2lnbmF0dXJlPC90ZXh0Pjwvc3ZnPg==';
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {(!(task as any).evidence || (task as any).evidence.evidenceCount === 0) && (
                          <div className="bg-gray-50 border border-gray-200 rounded p-2 mt-2">
                            <p className="text-gray-700 text-xs">No evidence found for this task</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {(task as any).evidence && (task as any).evidence.evidenceCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {(task as any).evidence.evidenceCount} Evidence
                          </Badge>
                        )}
                        <Badge variant="destructive">{task.status}</Badge>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Navigate to evidence modal or proof of posting page
                              window.open(`/proof-of-posting?taskId=${task.taskId}`, '_blank');
                            }}
                            className="text-xs h-8"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <input
                            type="checkbox"
                            checked={selectedTasks.includes(task.taskId)}
                            onChange={() => handleTaskSelection(task.taskId)}
                            className="h-4 w-4"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bug Prevention Info */}
      <Card>
        <CardHeader>
          <CardTitle>Bug Prevention Measures</CardTitle>
          <CardDescription>
            Systems in place to prevent this bug from occurring
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Fail-fast validation in main pack submission endpoint</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Fail-fast validation in legacy submit-pack-task endpoint</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Real-time posting event verification before status updates</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Comprehensive error logging and debugging</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Emergency detection and correction tools</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4">Confirm Task Correction</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to reset {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} 
              back to "ready_for_operator" status? This will allow operators to resubmit their evidence properly.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={correctTasksMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmCorrection}
                disabled={correctTasksMutation.isPending}
              >
                {correctTasksMutation.isPending ? 'Correcting...' : 'Yes, Correct Tasks'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
